<template>
  <li
    class="relative"
    :class="[
      enlargedMode ? enlargedContainerClass : tileContainerClass,
      { 'rec-shadow': streamRecording },
    ]"
  >
    <!-- Stream Device Icon -->
    <div
      class="
        bg-vice-teal-600
        rounded-full
        w-10
        h-10
        content-center
        justify-center
        absolute
        top-2
        left-2
        z-10
        tooltip
      "
    >
      <div class="flex justify-center">
        <ViceGlasses class="h-10 w-10" />
      </div>
      <span class="tooltip-text ml-2">Smartglasses</span>
    </div>

    <!-- Mute -->
    <ToggleMuteStream
      v-if="sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION"
      @stream-state-toggled="toggleStreamState"
      :isStreamMuted="streamMuted"
    />

    <!-- Snapshot Picture capturing -->
    <div class="absolute z-20 left-2 w-10 h-10 top-1/2 mt-negative-20px">
      <div
        v-if="supportSnapshots && sessionStore.snapshotsAllowed && shouldShowVuzixVideo"
        class="rounded-full w-full h-full flex justify-around"
      >
        <SnapshotButton
          :jitsiId="userJitsiId"
          :sessionId="sessionStore.sessionDBId"
          deviceName="Smartglasses"
          v-tippy="{
            content: 'Take Snapshot',
            theme: 'light-border',
            interactive: true,
          }"
        />
      </div>
    </div>

    <!-- Recording Timer -->
    <div class="absolute z-20 left-1/2 w-10 h-10 top-2 ml-negative-22px">
      <Timer v-if="streamRecording" :seconds="RECORDING_LIMIT_MINUTES * 60" />
    </div>

    <!-- Snapshot Video capturing -->
    <div class="absolute z-30 right-2 w-10 h-10 top-1/2 mt-negative-20px">
      <div
        v-show="supportRecording && sessionStore.videoSnapshotsAllowed && shouldShowVuzixVideo"
        class="rounded-full w-full h-full flex justify-around"
      >
        <VideoSnapshotButton
          :jitsiId="userJitsiId"
          deviceName="Smartglasses"
          :sessionId="sessionStore.sessionDBId"
          :sessionUniqueId="sessionStore.sessionUniqueId"
          :clinicianId="sessionStore.clinicianId"
          @isRecording="handleRecording"
          @recording-error="onRecordingError"
          v-tippy="{
            content: streamRecording ? 'Stop recording' : 'Start recording',
            theme: 'light-border',
            interactive: true,
          }"
        />
      </div>
    </div>

    <!-- Tile -->
    <div class="group block w-full h-full relative rounded-2xl overflow-visible">
      <div class="group block h-full relative rounded-2xl bg-black">
        <!-- Icon and trigger -->
        <div class="absolute top-0 right-0 z-10" v-if="isConsultationPhase">
          <div class="p-3 relative cursor-pointer" :class="{ 'sm:p-1': !enlargedMode }">
            <div class="relative flex items-center gap-2">
              <div
                data-cy="toggle-focus"
                :class="{ 'sm:p-1': !enlargedMode }"
                class="
                  bg-vice-dark-grey bg-opacity-70
                  text-white
                  rounded-full
                  flex
                  justify-center
                  items-center
                  p-2
                  hover:bg-opacity-90
                "
              >
                <ArrowsExpandIcon
                  class="h-6 w-6"
                  v-if="!enlargedMode"
                  :class="{ 'sm:w-5 sm:h-5': !enlargedMode }"
                  @click="onEnlargeIconClick(tileId)"
                />
                <ArrowsCollapseIcon class="p-0.5" @click="onMinimizeIconClick" v-else />
              </div>
            </div>
          </div>
        </div>

        <StreamPlayerRemote
          :streamMuted="streamMuted"
          :isAudioMuted="isAudioMuted"
          :jitsiUserId="userJitsiId"
          :setVideoAudioRefs="setVideoAudioRefs"
          :showVideo="shouldShowVuzixVideo"
        />

        <div
          class="justify-center items-center h-full w-full rounded-2xl bg-vice-medium-teal"
          v-show="shouldShowVuzixVideoOff"
        >
          <div class="flex justify-center h-full items-center">
            <ViceGlasses class="h-20 w-20" />
          </div>
        </div>

        <span
          v-if="streamMuted"
          class="muted-text w-full items-center justify-center rounded-xl z-5"
        >
          PAUSED
        </span>

        <UserLabel
          :class="{ 'text-xxs': enlargedJitsiId }"
          :isMutedMicrophone="!remoteDisplayInfo?.['audio'] || sessionStore.isPutOnHold"
          :isAudioMuted="isAudioMuted"
          :externalAudioIO="externalAudioIO"
          displayName="Smartglasses"
          :localStats="remoteDisplayInfo?.['connectionQuality']"
          @togglePreviewOverlay="toggleVuzixStream"
          @toggleVuzixAudio="toggleVuzixAudio"
          @toggleExternalAudioIO="toggleExternalAudioIO"
          @disconnectDevice="disconnectSmartglasses"
          :showDisconnectDevice="true"
          :isMultiCameraStream="true"
        />
      </div>
    </div>
    <SpeakingIndicator :audioLevel="remoteDisplayInfo.audioLevel" />
  </li>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { ArrowsExpandIcon } from '@heroicons/vue/outline';
import { directive } from 'vue-tippy';
import UserLabel from '@/Components/Session/Grid/UserLabel.vue';
import useSessionStore from '@/Stores/useSessionStore';
import StreamPlayerRemote from '@/Components/Session/Grid/StreamPlayerRemote.vue';
import ArrowsCollapseIcon from '@/Components/Icons/ArrowsCollapseIcon.vue';
import {
  MEDIA_TYPE,
  DISPLAY_NAME,
  SUPPORTS_SNAPSHOT,
  SUPPORTS_RECORDING,
  VICE_SESSION_PHASES,
  RECORDING_LIMIT_MINUTES,
} from '@/Utils/vice-constants';
import ToggleMuteStream from '@/Components/Inputs/ToggleMuteStream.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import SpeakingIndicator from '@/Components/Session/Grid/SpeakingIndicator.vue';
import ViceGlasses from '@/Components/Icons/ViceGlasses.vue';
import SnapshotButton from '@/Components/Buttons/SnapshotButton.vue';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';
import Timer from '@/Components/Loaders/Timer.vue';
import VideoSnapshotButton from '@/Components/Buttons/VideoSnapshotButton.vue';

export default {
  directives: {
    tippy: directive,
  },
  computed: {
    VICE_SESSION_PHASES() {
      return VICE_SESSION_PHASES;
    },
  },
  props: {
    index: {
      type: Number,
      required: true,
    },
    userJitsiId: {
      type: [Number, String],
    },
    enlargedJitsiId: {
      type: String,
    },
    remoteDisplayInfo: {
      type: Object,
      required: true,
    },
    muteMedia: {
      type: Function,
    },
    setVideoAudioRefs: {
      type: Function,
      required: true,
    },
    tileId: {
      type: String,
      required: false,
    },
    gridDimension: {
      type: Object,
      required: true,
    },
    isClinicianStream: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggleEnlargeMode'],
  components: {
    VideoSnapshotButton,
    Timer,
    SnapshotButton,
    ViceGlasses,
    UserLabel,
    ToggleMuteStream,
    ArrowsExpandIcon,
    SpeakingIndicator,
    ArrowsCollapseIcon,
    StreamPlayerRemote,
  },
  setup(props, { emit }) {
    const streamRecording = ref(false);
    const streamMuted = ref(false);
    const isAudioMuted = ref(true);
    const shouldDisplayVuzixStream = ref(true);

    const sessionStore = useSessionStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const supportSnapshots = computed(() => SUPPORTS_SNAPSHOT.includes('Smartglasses'));
    const supportRecording = computed(() => SUPPORTS_RECORDING.includes('Smartglasses'));

    const externalAudioIO = computed(() => multiCameraDevicesStore.externalAudioIO);
    const shouldShowVuzixVideo = computed(
      () =>
        props.remoteDisplayInfo?.video &&
        shouldDisplayVuzixStream.value &&
        !sessionStore.isPhasePaused,
    );

    watch(shouldShowVuzixVideo, (newVal) => {
      if (newVal) {
        const videoTrack = props.remoteDisplayInfo.tracks.find(
          (track) => track.type === MEDIA_TYPE.VIDEO,
        );
        // Add media stream to the store
        sessionStore.consultationStreams.Smartglasses = videoTrack.stream;
      } else {
        delete sessionStore.consultationStreams?.Smartglasses;
      }
    });

    const shouldShowVuzixVideoOff = computed(
      () =>
        !props.remoteDisplayInfo?.video ||
        !shouldDisplayVuzixStream.value ||
        sessionStore.isPhasePaused,
    );

    const disconnectSmartglasses = () => {
      sessionStore.deviceMessageSender.turnOffSmartGlass();
      multiCameraDevicesStore.removeExternalDevicePreview(DISPLAY_NAME.VUZIX);
      delete sessionStore.consultationStreams?.Smartglasses;
    };

    const toggleVuzixAudio = () => {
      isAudioMuted.value = !isAudioMuted.value;
      sessionStore.deviceMessageSender.toggleSmartglassAudioIO(isAudioMuted.value);
    };

    // Toggle using SG as audio input/output instead of the PC audio
    const toggleExternalAudioIO = () => {
      // If the current audio state does not match the desired state - toggle it
      if (multiCameraDevicesStore.externalAudioIO !== isAudioMuted.value) toggleVuzixAudio();
      multiCameraDevicesStore.externalAudioIO = !multiCameraDevicesStore.externalAudioIO;
    };

    const toggleVuzixStream = () => {
      shouldDisplayVuzixStream.value = !shouldDisplayVuzixStream.value;
    };

    const tileContainerClass = computed(
      () =>
        `col-start-${props.gridDimension[props.index].start ?? 1} col-span-${
          props.gridDimension[props.index].cols
        } row-span-${props.gridDimension[props.index].rows}`,
    );
    const enlargedContainerClass = 'col-start-2 col-span-10 row-span-8 row-start-1';
    const enlargedMode = computed(() => props.enlargedJitsiId === props.userJitsiId);

    const isConsultationPhase = ref(sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION);

    const onEnlargeIconClick = () => {
      emit('toggleEnlargeMode', props.userJitsiId);
    };

    const onMinimizeIconClick = () => {
      emit('toggleEnlargeMode');
    };

    const toggleStreamState = (e) => {
      streamMuted.value = e;
      props.muteMedia(props.userJitsiId, e);
    };

    const handleRecording = (state) => {
      streamRecording.value = state;
    };

    const onRecordingError = (error) => {
      console.error('Recording error:', error);
      // ToDo: human-friendly error msg
    };

    return {
      streamMuted,
      sessionStore,
      isAudioMuted,
      enlargedMode,
      streamRecording,
      externalAudioIO,
      supportSnapshots,
      supportRecording,
      tileContainerClass,
      isConsultationPhase,
      shouldShowVuzixVideo,
      enlargedContainerClass,
      shouldShowVuzixVideoOff,
      shouldDisplayVuzixStream,
      handleRecording,
      onRecordingError,
      toggleVuzixAudio,
      toggleVuzixStream,
      toggleStreamState,
      onEnlargeIconClick,
      onMinimizeIconClick,
      toggleExternalAudioIO,
      disconnectSmartglasses,
      RECORDING_LIMIT_MINUTES,
    };
  },
};
</script>
<style scoped>
.hide {
  display: none;
}
</style>
